<template>
  <div class="seminar-assignments-fi">
    <grid-container-fi v-if="assignmentFacets && lmsData && !isEditMode">
      <div class="fi-col-span-full">
        <div class="seminar-assignments-fi__facets">
          <facets-menu-fi
            :facets="assignmentFacets"
            :show-clear-all="Boolean(date)"
            custom-facet
            @change="facetChanged"
            @clear-all-facets="clearAllFacets"
          >
            <template #facets="{ facet, open, change, close, toggleOpen, idx, registerDomElement }">
              <seminar-facet-fi
                :date="date"
                :facet="facet"
                :open="open"
                :idx="idx"
                :register-dom-element="registerDomElement"
                @change="change"
                @toggle-open="toggleOpen(facet)"
                @close="close(facet)"
              />
            </template>
          </facets-menu-fi>
        </div>
      </div>
    </grid-container-fi>

    <grid-container-fi v-if="activeAssignments?.length && !isEditMode">
      <div class="fi-col-span-full">
        <div
          v-for="assignment in activeAssignments"
          :ref="assignment.assignmentId"
          :key="assignment.assignmentId"
          class="seminar-assignments-fi__assignment"
        >
          <div class="seminar-assignments-fi__assignment__image">
            <div
              v-if="assignment.targetGroup"
              class="seminar-assignments-fi__assignment__target-group"
            >
              {{ assignment.targetGroup }}
            </div>
            <cloud-image-fi
              v-if="assignment.image"
              class="seminar-assignments-fi__assignment__img"
              :src="assignment.image"
              params="&org_if_sml=0"
            />
            <img
              v-else
              class="seminar-assignments-fi__assignment__img fi-h-full fi-w-full fi-object-cover"
              :alt="$dict.get('my-Account.Order-History.No-Image-Available')"
              :title="$dict.get('my-Account.Order-History.No-Image-Available')"
              :src="placeholderImage"
            />
          </div>
          <div class="seminar-assignments-fi__assignment__info">
            <div class="seminar-assignments-fi__assignment__info__header">
              <div class="seminar-assignments-fi__assignment__info__status">
                <div class="status-label">
                  <div :class="['event-status', `event-status--${assignmentStatus(assignment)}`]">
                    <div class="event-status__tooltip">
                      <div
                        :class="['event-status', `event-status--${assignmentStatus(assignment)}`]"
                      />
                      {{ eventStatusText(assignmentStatus(assignment)) }}
                    </div>
                  </div>
                </div>
              </div>
              <h3
                class="seminar-assignments-fi__assignment__info__title"
                :data-seminar-id="assignment.assignmentId"
              >
                {{ assignment.title }}
              </h3>
              <div
                v-if="fields.infoDialogText?.value"
                class="seminar-assignments-fi__assignment__info__icon"
                @click="showInfoDialog"
              >
                <icon-fi icon="info" />
              </div>
            </div>
            <div
              v-if="assignment.teaser"
              class="seminar-assignments-fi__assignment__teaser"
            >
              <richtext-fi :html-content="{ value: assignment.teaser }" />
            </div>

            <div class="seminar-assignments-fi__assignment__more">
              <button-fi
                v-if="fields.moreInformationSeminar?.value && assignment.description"
                :class="{ 'button-fi--stretched': !isSM }"
                class="button-fi__tertiary"
                data-test="more-information-about-seminar"
                @click="openDetailAssignmentDialog(assignment)"
              >
                {{ fields.moreInformationSeminar.value }}
              </button-fi>
              <richtext-fi
                v-if="!loggedIn && fields.customText"
                :html-content="fields.customText"
                class="seminar-assignments-fi__assignment__more__register"
              />
            </div>
          </div>
          <div class="seminar-assignments-fi__assignment__assignments">
            <seminar-assignment-event-fi
              v-for="(event, idx) in assignment.events"
              :key="event.id"
              :event="event"
              :idx="idx as number"
              @register="onSubscribe(assignment, event.id)"
            />

            <seminar-assignment-inning-fi
              v-for="(inning, idx) in assignment.innings"
              :key="inning.id"
              :inning="inning"
              :idx="idx as number"
              @register="onSubscribe(assignment, inning.id)"
            />
          </div>
        </div>
      </div>
    </grid-container-fi>
    <grid-container-fi v-else>
      <div class="seminar-assignments-fi__no-results">
        <richtext-fi
          v-if="fields.noResultsSeminar"
          :html-content="fields.noResultsSeminar"
        />
      </div>
    </grid-container-fi>

    <seminar-info-dialog-fi
      v-if="fields.infoDialogText?.value"
      ref="seminarInfoDialog"
    />
    <seminar-detail-dialog-fi
      ref="seminarDetailDialog"
      :description="seminarDescription"
    />
  </div>
</template>

<script setup lang="ts">
import { ButtonFi, IconFi, RichtextFi } from 'atoms';
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';

import CloudImageFi from '@/components/atoms/cloud-image-fi/CloudImageFi.vue';
import FacetsMenuFi from '@/components/facets/facets-menu-fi/FacetsMenuFi.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import SeminarAssignmentEventFi from '@/components/seminar-assignments-fi/seminar-assignment-event-fi/SeminarAssignmentEventFi.vue';
import SeminarAssignmentInningFi from '@/components/seminar-assignments-fi/seminar-assignment-inning-fi/SeminarAssignmentInningFi.vue';
import SeminarDetailDialogFi from '@/components/seminar-assignments-fi/seminar-detail-dialog-fi/SeminarDetailDialogFi.vue';
import SeminarFacetFi from '@/components/seminar-assignments-fi/seminar-facet-fi/SeminarFacetFi.vue';
import { useAssetUrl } from '@/composables/assetUrl';
import { useBreakpoints } from '@/composables/breakpoint';
import { useDate } from '@/composables/date';
import { useFields } from '@/composables/fields';
import { useDict, useFischer } from '@/composables/fischerPlugin';
import { useLogin } from '@/composables/login';
import { usePriceCurrency } from '@/composables/priceCurrency';
import { useSmoothScroll } from '@/composables/smoothScroll';
import { useNotificationStore } from '@/store/pinia/notification';
import { useProfileFlyoutStore } from '@/store/pinia/profile-flyout';

import SeminarInfoDialogFi from './seminar-info-dialog-fi/SeminarInfoDialogFi.vue';
import { usePendingRegistration } from './usePendingRegistration';
import { useSeminarRestore } from './useSeminarRestore';

defineOptions({ name: 'SeminarAssignmentsFi' });

const props = withDefaults(
  defineProps<{
    lmsData?: object | null;
  }>(),
  {
    lmsData: null,
  },
);

const emit = defineEmits<{
  'filters-changed': [];
  register: [payload: any];
}>();

const { initializeAuthorization } = useLogin();
const { saveSeminarRestore } = useSeminarRestore();
const { savePendingRegistration, consumePendingRegistration } = usePendingRegistration();
const { formatPrice } = usePriceCurrency();
const { getAssetUrl } = useAssetUrl();
const fischer = useFischer();
const dict = useDict();
const fields = useFields('SeminarsOverviewFiFields');
const { openGeneralErrorNotification } = useNotificationStore();
const { smoothScrollToElement } = useSmoothScroll();
const { isSM } = useBreakpoints();
const { preventProfileFlyout } = useProfileFlyoutStore();

const { formatDate, formatTime, formatDateIsoShort, parseDate, momentLoadedPromise } = useDate();

const placeholderImage = getAssetUrl('/img/products/placeholder.svg');
// TODO: any
const assignments = ref<any[] | null>(null);
const filteredAssignments = ref<any[] | null>(null);
const assignmentFacets = ref<any[] | null>(null);
const activeFilters = ref<any[]>([]);
const date = ref<any>(null);
const activeAssignment = ref<any>(null);
const seminarDescription = ref<any>(null);

const dateFacet = ref<any>({
  type: 'date',
  name: '',
  elements: [
    {
      type: 'date',
      date: null,
      selected: false,
    },
  ],
});

const seminarInfoDialog = useTemplateRef<any>('seminarInfoDialog');
const seminarDetailDialog = useTemplateRef<any>('seminarDetailDialog');

const sortByDate = computed(() => Boolean(fields.allowSortByDate?.value));

const activeAssignments = computed(() => {
  const list = filteredAssignments.value ?? assignments.value;
  if (!sortByDate.value || !list) {
    return list;
  }
  return [...list].sort((a, b) => getEarliestAssignmentDate(a) - getEarliestAssignmentDate(b));
});

const loggedIn = computed(() => fischer.user.authenticated());

// LMS (Avendoo MOVE editor) chrome / non-content nodes that must never be rendered.
const LMS_NOISE_SELECTORS = [
  '.button_dialog_remove',
  '.info_visibility',
  '.FN_DragHandle',
  '.column_wrapper_header',
  '.displayNonePreview',
  '.elementBackgroundColor',
  'input[type="hidden"]',
  'script',
  'style',
].join(',');

const LMS_DANGER_COLOR = /#e74c3c|#ff0000|#f00(?=[;\s"'])|rgb\(\s*255\s*,\s*0\s*,\s*0/;

const openDetailAssignmentDialog = (assignment: any) => {
  activeAssignment.value = assignment;

  const parser = new DOMParser();
  const doc = parser.parseFromString(activeAssignment.value?.description || '', 'text/html');

  doc.querySelectorAll<HTMLElement>('[style*="background-image"]').forEach((el) => {
    (el.closest('.FN_SequenceTextElement') ?? el).remove();
  });

  doc.querySelectorAll<HTMLElement>('[style*="color"]').forEach((el) => {
    if (LMS_DANGER_COLOR.test((el.getAttribute('style') || '').toLowerCase())) {
      (el.closest('h1, h2, h3, h4') ?? el).classList.add('seminar-detail-dialog-fi__danger');
    }
  });

  doc.querySelectorAll(LMS_NOISE_SELECTORS).forEach((el) => el.remove());

  doc.querySelectorAll<HTMLElement>('*').forEach((el) => {
    el.removeAttribute('style');
    el.removeAttribute('contenteditable');
    el.removeAttribute('aria-readonly');
    [...el.attributes].forEach((attr) => {
      if (attr.name.startsWith('on')) {
        el.removeAttribute(attr.name);
      }
    });
  });

  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null);
  let node;
  while ((node = walker.nextNode() as Text)) {
    node.nodeValue = node.nodeValue?.trim().replace(/\s+/g, ' ') || '';
  }

  doc
    .querySelectorAll('.column_wrapper[data-columns="2"]')
    .forEach((el) => el.classList.add('seminar-detail-dialog-fi__grid'));
  doc.querySelectorAll('.columnWidthBig, .span8').forEach((el) => {
    el.classList.add('seminar-detail-dialog-fi__main');
    el.closest('.row-fluid')?.classList.add('seminar-detail-dialog-fi__grid');
  });
  doc
    .querySelectorAll('.columnWidthSmall, .span4')
    .forEach((el) => el.classList.add('seminar-detail-dialog-fi__aside'));

  doc
    .querySelectorAll('.element_title_and_text_text')
    .forEach((el) => el.classList.add('seminar-detail-dialog-fi__intro'));

  doc.querySelectorAll('li > h3, li > h4').forEach((heading) => {
    const li = heading.parentElement;
    const list = li?.parentElement;
    if (list && (list.tagName === 'UL' || list.tagName === 'OL') && list.children.length === 1) {
      list.replaceWith(heading);
    } else if (li) {
      li.replaceWith(heading);
    }
  });

  doc.querySelectorAll('a.dooBtn').forEach((el) => {
    el.classList.remove('dooBtn', 'orange', 'hoverColor1', 'animation');
    el.classList.add('button-fi__primary');
  });

  const mediaBucket =
    APP_ENVIRONMENT !== 'PROD' && APP_ENVIRONMENT !== 'UAT'
      ? '_lms-media-int_'
      : '_lms-media-prod_';
  doc.querySelectorAll('img').forEach((img) => {
    const mediaImg = (img.getAttribute('src') || '').match(/media\/([^/]+)/)?.[1];
    if (mediaImg) {
      img.setAttribute(
        'src',
        `https://media.fischer.group/v7/${mediaBucket}/${mediaImg}/mediadata`,
      );
    }
  });

  if (fields.registerSeminar?.value) {
    const buttonDiv = document.createElement('div');
    buttonDiv.classList.add('seminar-detail-dialog-fi__register');
    const button = document.createElement('button');
    button.classList.add('button-fi__primary');
    button.innerText = fields.registerSeminar.value;
    button.setAttribute('data-seminar-register', 'true');
    buttonDiv.appendChild(button);
    const registerTarget =
      doc.querySelector('.seminar-detail-dialog-fi__main .element_text_text') ||
      doc.querySelector('.seminar-detail-dialog-fi__main') ||
      doc.querySelector('.element_text_text') ||
      doc.body;
    registerTarget.appendChild(buttonDiv);
  }

  let bodyContent = doc.body.innerHTML;
  bodyContent = bodyContent.replaceAll('$ObjectTitle$', assignment.title ?? '');
  bodyContent = bodyContent.replaceAll('$ObjectTeaser$', assignment.teaser ?? '');
  seminarDescription.value = bodyContent || '';

  seminarDetailDialog.value?.open();
  nextTick(() => {
    const btn = document.querySelector('[data-seminar-register]');
    if (btn) {
      btn.addEventListener('click', () => {
        showRegister(assignment);
      });
    }
  });
};

const showRegister = (assignment: any, eventId?: string) => {
  emit('register', { assignment, eventId });
};

const onSubscribe = (assignment: any, id?: string) => {
  if (loggedIn.value) {
    if (fields.enableRegistration?.value) {
      showRegister(assignment, id);
    } else {
      console.debug('redirecting to assignment from link');
      window.location.href = assignment.link;
    }
    return;
  }
  savePendingRegistration({ assignmentId: assignment.assignmentId, eventId: id });
  saveSeminarRestore(window.location.search);
  preventProfileFlyout();
  initializeAuthorization(true, `assignment:${assignment.assignmentId}`);
};

const handleLmsDataChanges = async () => {
  try {
    await momentLoadedPromise;
    setAssignments();
  } catch (error) {
    console.error('error in lms-data handler', error);
  }
};

const setFacets = () => {
  const facets: any[] = [dateFacet.value];
  assignments.value?.forEach((assignment, assignmentIndex) => {
    const { assignmentId } = assignment;

    if (assignment.classifications) {
      assignment.classifications.forEach((facet: any, classificationIndex: number) => {
        const { key, value } = facet;
        const facetIndex = facets.findIndex((e) => e.name === key);

        if (facets.length && facetIndex > -1) {
          const facetElement = facets[facetIndex];
          const elementIndex = facetElement.elements.findIndex((e: any) => e.name === value);

          if (elementIndex > -1) {
            facetElement.elements[elementIndex].assignmentIds.push(assignmentId);
            facetElement.elements[elementIndex].count += 1;
          } else {
            facets[facetIndex].elements.push({
              facetId: Number(`${assignmentIndex}${classificationIndex}`),
              assignmentIds: [assignmentId],
              name: facet.value,
              selected: false,
              count: 1,
            });
          }
        } else {
          facets.push({
            multiselect: true,
            name: facet.key,
            elements: [
              {
                facetId: Number(`${assignmentIndex}${classificationIndex}`),
                assignmentIds: [assignmentId],
                name: facet.value,
                count: 1,
                selected: false,
              },
            ],
          });
        }
      });
    }
  });

  assignmentFacets.value = JSON.parse(JSON.stringify(facets));
};

const setUrlFilters = async (filters: Record<string, any> = {}) => {
  await momentLoadedPromise;
  setFacets();
  Object.entries(filters)?.forEach(([name, value]) => {
    if (name === dateFacet.value.name) {
      const [startString, endString] = value?.split(' - ') || [];
      if (!startString || !endString) {
        console.error('missing start-end', value);
        return;
      }
      const startDate = parseDate(startString);
      const endDate = parseDate(endString);
      if (!startDate || !endDate) {
        console.error('Could not parse dates', startDate, endDate);
        openGeneralErrorNotification();
        return;
      }
      facetChanged({
        value: true,
        type: 'date',
        element: {
          date: {
            start: formatDateIsoShort(startDate),
            end: formatDateIsoShort(endDate),
          },
        },
      });
    } else {
      const oldFacet = assignmentFacets.value?.find((facet) => facet.name === name);
      if (!oldFacet) {
        console.error('Could not find old seminar facet to restore', name);
        return;
      }
      const element = oldFacet?.elements.find((item: any) => item.name === value);
      if (!element) {
        console.error('Could not find seminar element', name, value);
        return;
      }
      facetChanged({
        name,
        value: true,
        selected: true,
        element,
      });
    }
  });
};

const setAssignments = () => {
  const assignmentsList: any[] = [];

  ((props.lmsData as any)?.assignments?.assignment || []).forEach((el: any) => {
    const assignmentId = el.assignment.assignmentId[0].id;
    const image = el.picture?.href && getProxySrc(el.picture.href);
    const classifications = el.classifications?.classification;
    const targetGroupKeyId = Number(fields.targetGroupKeyId?.value) || 6;
    const targetGroup =
      classifications?.find((el: any) => el.keyId === targetGroupKeyId)?.value || null;
    const { title, teaser } = el;

    const assignment: any = {
      assignmentId,
      title,
      teaser,
      link: el.deeplink?.href,
      image,
      classifications,
      targetGroup,
    };

    let innings: any[] = [];
    let events: any[] = [];
    if (el.innings.inning.length) {
      innings = getAssignmentInnings(el.innings.inning);
      assignment.description = innings?.[0]?.seminars?.[0]?.description;
    } else {
      events = getAssignmentEvents(assignmentId);
      if (events.length) {
        const seminarId = events[0]?.seminarId;
        const seminar = (props.lmsData as any).seminars.seminar.find(
          (element: any) => element.seminar.seminarId[0].id === seminarId,
        );
        assignment.description = seminar.description;
      }
    }

    if (innings.length || events.length) {
      assignmentsList.push({ ...assignment, events, innings });
    }
  });

  assignments.value = assignmentsList;
  setFacets();
};

const getAssignmentEvents = (assignmentId: string) => {
  const filteredEvents = (props.lmsData as any).events.event.filter(
    (element: any) => element.assignment.eventAssignmentId[0].id === assignmentId,
  );
  const assignmentEvents: any[] = [];

  if (filteredEvents.length) {
    filteredEvents.forEach((el: any) => {
      assignmentEvents.push(getEventData(el));
    });
  }

  return assignmentEvents;
};

const getAssignmentInnings = (innings: any[]) => {
  const assignmentInnings = innings.map((el: any) => {
    const {
      name,
      startDate,
      endDate,
      maxParticipants,
      registeredParticipants,
      seminarEvents,
      language,
    } = el;

    const price = getInningPriceString(el);
    const { start, end, deadline } = getEventDate(
      new Date(startDate).getTime(),
      new Date(endDate).getTime(),
    );

    let inningSeminars = null;
    if (seminarEvents?.id) {
      const eventIds = seminarEvents.id.map((el: any) => el.inningSeminarEventId[0].id);
      inningSeminars = getInningSeminars(eventIds);
    }

    const externalDescription = inningSeminars?.[0]?.externalDescription;

    return {
      id: el.inningID.id[0].inningId[0].id,
      name,
      start,
      end,
      deadline,
      price,
      maxParticipants,
      language,
      actualParticipants: registeredParticipants,
      seminars: inningSeminars,
      externalDescription,
    };
  });

  return assignmentInnings.sort((a: any, b: any) => {
    const dateA = new Date(a.start.dateTimeLong);
    const dateB = new Date(b.start.dateTimeLong);
    return (dateA as any) - (dateB as any);
  });
};

const facetChanged = (facet: any) => {
  if (facet) {
    if (facet.element.date) {
      date.value = facet.value ? facet.element.date : null;
    } else if (facet.value) {
      activeFilters.value.push(facet.element);
    } else {
      let index;
      if (facet.element?.name) {
        index = activeFilters.value.findIndex((filter) => filter.name === facet.element.name);
      } else if (facet.name) {
        index = activeFilters.value.indexOf(facet.element);
      } else {
        console.error('Could not unselect facet', facet);
        return;
      }
      if (index === -1) {
        console.error('Did not find facet', JSON.stringify(facet));
        return;
      }
      activeFilters.value.splice(index, 1);
    }
  }

  filterAssignments();
  emit('filters-changed');
};

const filterAssignments = () => {
  const newFilteredAssignments: any[] = [];

  assignments.value?.forEach((assignment) => {
    let isActive = true;

    activeFilters.value.forEach((activeFilter) => {
      let f = false;
      activeFilter.assignmentIds.forEach((id: string) => {
        if (id === assignment.assignmentId) {
          f = true;
        }
      });

      if (!f) {
        isActive = false;
      }
    });

    if (isActive) {
      if (date.value) {
        const selectedStartDate = new Date(date.value.start).setHours(0, 0, 0, 0);
        const selectedEndDate = new Date(date.value.end).setHours(0, 0, 0, 0);
        const filteredEvents: any[] = [];

        assignment.events.forEach((event: any) => {
          const eventStartDate = new Date(Number(event.start.dateTimeLong)).setHours(0, 0, 0, 0);
          let eventEndDate = new Date(Number(event.end.dateTimeLong)).setHours(0, 0, 0, 0);

          if (event.followEvents) {
            eventEndDate = new Date(
              Number(event.followEvents[event.followEvents.length - 1].end.dateTimeLong),
            ).setHours(0, 0, 0, 0);
          }

          if (date.value.start && date.value.end) {
            if (selectedStartDate <= eventStartDate && selectedEndDate >= eventEndDate) {
              filteredEvents.push(event);
            }
          } else if (selectedStartDate <= eventStartDate) {
            filteredEvents.push(event);
          }
        });
        if (filteredEvents.length) {
          newFilteredAssignments.push({ ...assignment, events: filteredEvents });
        }
      } else {
        newFilteredAssignments.push(assignment);
      }
    }
  });

  filteredAssignments.value = newFilteredAssignments;
  updateFacets();
};

const updateFacets = () => {
  assignmentFacets.value?.forEach((facet) => {
    if (facet.type === 'date') {
      if (date.value) {
        const dateVal = getEventDate(date.value.start, date.value.end);
        const dateString = dateVal.end.day
          ? `${dateVal.start.day} - ${dateVal.end.day}`
          : `${dateVal.start.day}`;

        facet.elements[0].date = dateVal;
        facet.elements[0].name = dateString;
        facet.elements[0].selected = true;
      } else {
        facet.elements[0].date = null;
        facet.elements[0].selected = false;
      }
    } else {
      facet.elements.forEach((element: any) => {
        let counter = 0;
        filteredAssignments.value?.forEach((assignment) => {
          element.assignmentIds.forEach((assignmentId: string) => {
            if (assignmentId === assignment.assignmentId) {
              counter += 1;
            }
          });
        });

        element.selected = activeFilters.value.includes(element);
        element.count = counter;
        element.disabled = counter === 0;
      });
    }
  });
};

const clearAllFacets = () => {
  filteredAssignments.value = null;
  date.value = null;
  activeFilters.value = [];
  setFacets();
  emit('filters-changed');
};

const getInningSeminars = (eventIds: string[]) => {
  const seminars: any[] = [];
  eventIds.forEach((eventId) => {
    const filteredEvents = (props.lmsData as any).events.event.filter(
      (element: any) => element.event.eventId[0].id === eventId,
    );

    if (filteredEvents.length) {
      const inningEvents: any[] = [];
      const { title, description, externalDescription } = (
        props.lmsData as any
      ).seminars.seminar.find(
        (element: any) =>
          element.seminar.seminarId[0].id === filteredEvents[0].seminar.eventSeminarId[0].id,
      );

      filteredEvents.forEach((el: any) => {
        inningEvents.push(getEventData(el));
      });

      seminars.push({ title, description, externalDescription, events: inningEvents });
    }
  });

  return seminars.sort((a: any, b: any) => {
    const dateA = new Date(a.events[0].start.dateTimeLong);
    const dateB = new Date(b.events[0].start.dateTimeLong);
    return (dateA as any) - (dateB as any);
  });
};

const getEventData = (rawEvent: any) => {
  const location = getEventLocation(rawEvent);
  const { start, end, deadline } = getEventDate(
    Number(rawEvent.start.dateTimeLong),
    Number(rawEvent.end.dateTimeLong),
  );
  const price = getEventPriceString(rawEvent);
  const followEvents = getFollowEvents(rawEvent);
  const { maxParticipants, actualParticipants, description, language } = rawEvent;
  const seminarId = rawEvent.seminar.eventSeminarId[0].id;
  const seminar = (props.lmsData as any).seminars.seminar.find(
    (element: any) => element.seminar.seminarId[0].id === seminarId,
  );

  return {
    id: rawEvent.event.eventId[0].id,
    assignmentId: rawEvent.assignment.eventAssignmentId[0].id,
    seminarId,
    location,
    language,
    start,
    end,
    deadline,
    description,
    price,
    followEvents,
    maxParticipants,
    actualParticipants,
    externalDescription: seminar.externalDescription,
  };
};

const getEventLocation = (el: any) => {
  const addressInformation = el.location.placeLink[0]?.location?.addressInformation;

  if (addressInformation) {
    const { street, city, postcode, region } = addressInformation;
    return { type: 'onsite', street, city, postcode, region };
  }

  return { type: 'online' };
};

const getEventDate = (start: any, end: any) => {
  const startTime = formatTime(start);
  const endTime = formatTime(end);
  const startDay = formatDate(start);
  const endDay = formatDate(end);
  const deadlineDay = new Date(start);
  deadlineDay.setDate(deadlineDay.getDate() - 7);

  return {
    start: { dateTimeLong: start, day: startDay, time: startTime },
    end: { dateTimeLong: end, day: endDay, time: endTime },
    deadline: formatDate(deadlineDay),
  };
};

const getInningPriceString = (el: any) => {
  const { price, currency } = el;
  if (price && price !== 0) {
    return formatPrice(price, currency);
  }
  return fields.freeOfChargeLabel?.value;
};

const getEventPriceString = (el: any) => {
  const { price, currency } = el.prices?.price?.[0] || {};
  if (price && price !== 0) {
    return formatPrice(price, currency);
  }
  return fields.freeOfChargeLabel?.value;
};

const getFollowEventDate = (start: any, end: any) => {
  const startTime = formatTime(start);
  const endTime = formatTime(end);
  const day = formatDate(start);

  return {
    start: { dateTimeLong: start, day, time: startTime },
    end: { dateTimeLong: end, day, time: endTime },
  };
};

const getFollowEvents = (el: any) => {
  if (el.followEvents?.followEvent) {
    const date = getFollowEventDate(Number(el.start.dateTimeLong), Number(el.end.dateTimeLong));

    if (el.followEvents.followEvent) {
      const events = [
        {
          ...date,
          id: el.event.eventId[0].id,
          price: getEventPriceString(el.followEvents.followEvent[0]),
          location: getEventLocation(el.followEvents.followEvent[0]),
        },
      ];

      el.followEvents.followEvent.forEach((followEvent: any) => {
        const followEventDate = getFollowEventDate(
          Number(followEvent.start.dateTimeLong),
          Number(followEvent.end.dateTimeLong),
        );
        events.push({
          ...followEventDate,
          id: followEvent.event.followEventId[0].id,
          price: getEventPriceString(followEvent),
          location: getEventLocation(followEvent),
        });
      });

      return events;
    }
  }

  return null;
};

const getEarliestAssignmentDate = (assignment: any) => {
  const dates = [
    ...(assignment.events?.map((e: any) => Number(e.start.dateTimeLong)) || []),
    ...(assignment.innings?.map((i: any) => Number(i.start.dateTimeLong)) || []),
  ];
  return dates.length ? Math.min(...dates) : Infinity;
};

const getProxySrc = (url: string) => {
  if (!url) return null;
  if (APP_ENVIRONMENT !== 'PROD' && APP_ENVIRONMENT !== 'UAT') {
    return url.replace('https://testfischerwerke.avendoo.de/api/v1/media', '_lms-media-int_');
  }
  return url.replace('https://fischeracademy.com/api/v1/media', '_lms-media-prod_');
};

const assignmentStatus = (assigment: any) => {
  let status = 'full';

  assigment.events?.forEach((element: any) => {
    const maxParticipants = element.maxParticipants;
    const actualParticipants = element.actualParticipants;

    if ((100 * actualParticipants) / maxParticipants > 25) {
      status = 'partly';
    } else if (actualParticipants < maxParticipants) {
      status = 'free';
    }
  });

  assigment.innings?.forEach((element: any) => {
    const maxParticipants = element.maxParticipants;
    const actualParticipants = element.actualParticipants;

    if ((100 * actualParticipants) / maxParticipants > 25) {
      status = 'partly';
    } else if (actualParticipants < maxParticipants) {
      status = 'free';
    }
  });

  return status;
};

const eventStatusText = (eventStatus: string) => {
  if (eventStatus === 'full') return fields.seminarStatusFull?.value;
  if (eventStatus === 'partly') return fields.seminarStatusPartly?.value;
  return fields.seminarStatusFree?.value;
};

const showInfoDialog = () => {
  seminarInfoDialog.value?.open();
};

const scrollTo = (id: string, duration = 0) => {
  const el = document.querySelector(`[data-seminar-id="${id}"]`);
  if (id && el && activeAssignments.value?.length) {
    smoothScrollToElement(el, { duration });
  }
};

watch(
  () => props.lmsData,
  (newLmsData, oldLmsData) => {
    if (newLmsData !== oldLmsData) {
      handleLmsDataChanges();
    }
  },
  { immediate: true },
);

watch([assignments, loggedIn], () => {
  if (!assignments.value?.length || IS_SSR || !loggedIn.value) {
    return;
  }
  const pending = consumePendingRegistration();
  if (!pending) {
    return;
  }
  const assignment = assignments.value?.find((item) => item?.assignmentId === pending.assignmentId);
  if (assignment) {
    onSubscribe(assignment, pending.eventId);
  }
});

onMounted(() => {
  dateFacet.value.name = dict.get('global-Labels.Date-Range');
});

defineExpose({ scrollTo, setUrlFilters, assignmentFacets });
</script>

<style scoped src="./seminar-assignments-fi" />
